import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import momentTz from "moment-timezone";
import { Col, Row, Button } from "react-bootstrap";
import { addFromDate, addToDate, setFromRawDate, setFromTimestamp, setToRawDate, setToTimestamp } from "./../../Slices/sessionSlice";
import { newAlert } from "./../../Slices/alertSlice";
import DateTimePickerComponent from "../../components/DateTimePicker";
import TimeZonePicker from "../TimeZonePicker/TimeZonePicker";
import MonthPicker from "../../components/MonthPicker/MonthPicker";
import { calculateDateRange, getDateStringFromTimestamp, getTimestampFromDateTime } from "../../helpers/stubs";
import styles from "./TimeLinePicker.module.css";

const TimeLinePickerContainer = (props) => {
  const { fGettingUsedInOrgDashboard = false } = props;
  //#region Store selectors
  const { fromRaw, toRaw, toTimestamp, fromTimestamp } = useSelector((state) => state.sessionSlice);
  const { sessionUser: { timezone } } = useSelector((state) => state.sessionSlice);
  const [selectedTimeline, setSelectedTimeline] = useState("This Week");
  const [selectedMonth, setSelectedMonth] = useState(() => 
    timezone ? momentTz().tz(timezone) : momentTz()
  );
  const dispatch = useDispatch();
  //#endregion

  // Helper function to get date string in the specified timezone
  const getDateStringInTimezone = (date, timezone) => {
    const result = momentTz(date).tz(timezone).format('YYYY-MM-DD');
    return result;
  };

  // Handle month change from MonthPicker
  const handleMonthChange = (newMonth) => {
    const momentMonth = momentTz(newMonth).tz(timezone);
    setSelectedMonth(momentMonth);
    setSelectedTimeline("Custom Month");
    
    // Calculate start and end of the selected month
    const startOfMonth = momentMonth.clone().startOf('month');
    const endOfMonth = momentMonth.clone().endOf('month');
    
    // Update timestamps for display
    const fromTimestamp = getDateStringFromTimestamp(startOfMonth.toDate(), timezone);
    const toTimestamp = getDateStringFromTimestamp(endOfMonth.toDate(), timezone);
    dispatch(setFromTimestamp(fromTimestamp));
    dispatch(setToTimestamp(toTimestamp));
    
    // Update raw dates
    dispatch(setFromRawDate(startOfMonth.toISOString()));
    dispatch(setToRawDate(endOfMonth.toISOString()));
    
    // Update final dates for API calls
    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(startOfMonth.toDate(), timezone), "00:00", timezone);
    const toTime = getTimestampFromDateTime(getDateStringInTimezone(endOfMonth.toDate(), timezone), "23:59", timezone);
    dispatch(addFromDate(fromTime));
    dispatch(addToDate(toTime));
  };

  //#region Set This Week As Default
  useEffect(() => {
    const { from, to } = calculateDateRange("this week", timezone);
    const toTimestamp = getDateStringFromTimestamp(to, timezone);
    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
    dispatch(setFromTimestamp(fromTimestamp))
    dispatch(setToTimestamp(toTimestamp))
    setSelectedTimeline("This Week");
    dispatch(setFromRawDate(from.toISOString()));
    dispatch(setToRawDate(to.toISOString()));
    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
    dispatch(addToDate(toTime));
    dispatch(addFromDate(fromTime));
    // Initialize selected month to current month
    if (timezone) {
      setSelectedMonth(momentTz().tz(timezone));
    }
  }, [dispatch, timezone]);
  //#endregion

  return (
    <>
      <Row className={`text-start`}>
        <Col sm={"6"}>
          <Row>
            <Col sm={"4"} className={"p-0 text-center"}>
              <Col className={"justify-content-center"}>
                <span className={"p-0"}>{"From"}</span>
              </Col>
              <Col className={`justify-content-start`}>
                <DateTimePickerComponent
                  value={fromRaw ? (() => {
                    // Create a date that represents the intended date in local time
                    // fromTimestamp contains the correct date string (e.g., "Wed, 01 Oct 2025")
                    if (fromTimestamp) {
                      // Extract date parts from the display string
                      const match = fromTimestamp.match(/(\d{2}) (\w{3}) (\d{4})/);
                      if (match) {
                        const [, day, monthStr, year] = match;
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const month = months.indexOf(monthStr);
                        return new Date(parseInt(year), month, parseInt(day));
                      }
                    }
                    return new Date(fromRaw);
                  })() : new Date()}
                  onChangeHandler={(val) => {
                    const fromDateISO = moment(val).tz("Zulu").toISOString();
                    if (toRaw && moment(toRaw).isBefore(fromDateISO, "day")) {
                      dispatch(
                        newAlert({
                          type: "danger",
                          message: (
                            <span>From Date Cannot Be After To Date</span>
                          ),
                        })
                      );
                      return;
                    }
                    const fromRawProcessed = getDateStringFromTimestamp(val, timezone);
                    dispatch(setFromTimestamp(fromRawProcessed));
                    dispatch(setFromRawDate(val.toISOString()));
                    // Use the local date components to avoid timezone conversion issues
                    const localDateString = moment(val).format('YYYY-MM-DD');
                    const fromTime = getTimestampFromDateTime(localDateString, "00:00", timezone);
                    dispatch(addFromDate(fromTime));
                  }}
                  dateTimestamp={fromTimestamp}
                />
              </Col>
            </Col>
            <Col sm={"4"} className={"p-0 text-center"}>
              <Col className={"justify-content-center"}>
                <span className="p-0">{"To"}</span>
              </Col>
              <Col className={`justify-content-center`}>
                <DateTimePickerComponent
                  className={"w-100"}
                  value={toRaw ? (() => {
                    // Create a date that represents the intended date in local time
                    // toTimestamp contains the correct date string (e.g., "Fri, 31 Oct 2025")
                    if (toTimestamp) {
                      // Extract date parts from the display string
                      const match = toTimestamp.match(/(\d{2}) (\w{3}) (\d{4})/);
                      if (match) {
                        const [, day, monthStr, year] = match;
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const month = months.indexOf(monthStr);
                        return new Date(parseInt(year), month, parseInt(day));
                      }
                    }
                    return new Date(toRaw);
                  })() : new Date()}
                  onChangeHandler={(val) => {
                    const toDateISO = moment(val).tz("Zulu").toISOString();
                    if (
                      fromRaw &&
                      moment(fromRaw).isAfter(toDateISO, "day")
                    ) {
                      dispatch(
                        newAlert({
                          type: "danger",
                          message: (
                            <span>To Date Cannot Be Before From Date</span>
                          ),
                        })
                      );
                      return;
                    }
                    const toTimestampProcessed = getDateStringFromTimestamp(val, timezone);
                    dispatch(setToRawDate(val));
                    dispatch(setToTimestamp(toTimestampProcessed))
                    // Use the local date components to avoid timezone conversion issues
                    const localToDateString = moment(val).format('YYYY-MM-DD');
                    const toTime = getTimestampFromDateTime(localToDateString, "23:59", timezone);
                    dispatch(addToDate(toTime));
                  }}
                  dateTimestamp={toTimestamp}
                />
              </Col>
            </Col>
            <Col sm="4" className="text-center">
              <TimeZonePicker />
            </Col>
          </Row>
        </Col>

        <Col sm={"5"}>
          <Row>
            <Row className={"justify-content-between"}>
              <Col sm={"auto"} style={{ paddingRight: "38px" }}>
                <Button
                  variant={selectedTimeline === "Today" ? "link" : "null"}
                  onClick={() => {
                    setSelectedTimeline("Today");
                    const { from, to } = calculateDateRange("today", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  {"Today"}
                </Button>
              </Col>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "This Week" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("this week", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    setSelectedTimeline("This Week");
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));

                  }}
                >
                  This week
                </Button>
              </Col>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "Next Week" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("next week", timezone);
                    console.log(from, to, "nextweek")
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    console.log(from, to, "nextweek")
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    setSelectedTimeline("Next Week");
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  Next week
                </Button>
              </Col>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "Last Month" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("last month", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    setSelectedTimeline("Last Month");
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  Last month
                </Button>
              </Col>

            </Row>
            <Row className={"justify-content-between"}>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "Yesterday" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("yesterday", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    setSelectedTimeline("Yesterday");
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  Yesterday
                </Button>
              </Col>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "Last Week" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("last week", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    console.log(from, to, "lastweek")
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    setSelectedTimeline("Last Week");
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  Last week
                </Button>
              </Col>
              <Col sm={"auto"}>
                <Button
                  variant={selectedTimeline === "This Month" ? "link" : "null"}
                  onClick={() => {
                    const { from, to } = calculateDateRange("this month", timezone);
                    const toTimestamp = getDateStringFromTimestamp(to, timezone);
                    const fromTimestamp = getDateStringFromTimestamp(from, timezone);
                    dispatch(setFromTimestamp(fromTimestamp))
                    dispatch(setToTimestamp(toTimestamp))
                    dispatch(setFromRawDate(from.toISOString()));
                    dispatch(setToRawDate(to.toISOString()));
                    setSelectedTimeline("This Month");
                    const fromTime = getTimestampFromDateTime(getDateStringInTimezone(from, timezone), "00:00", timezone);
                    const toTime = getTimestampFromDateTime(getDateStringInTimezone(to, timezone), "23:59", timezone);
                    dispatch(addToDate(toTime));
                    dispatch(addFromDate(fromTime));
                  }}
                >
                  This month
                </Button>
              </Col>
              <Col sm={"3"} className="text-center">
                <div 
                  className={styles.monthPickerWrapper}
                  style={{ 
                    color: selectedTimeline === "Custom Month" ? "#0d6efd" : "inherit",
                  }}
                >
                  <MonthPicker 
                    month={selectedMonth ? selectedMonth.toISOString() : momentTz().tz(timezone).toISOString()} 
                    onMonthChange={handleMonthChange}
                    fGettingUsedInOrgDashboard={true}
                  />
                </div>
              </Col>
            </Row>
          </Row>
        </Col>
      </Row>
    </>
  );
};

//#region
TimeLinePickerContainer.propTypes = {};
//#endregion

export default TimeLinePickerContainer;
